import React, { Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import { Alert, PageSection } from "@patternfly/react-core";
import { accessibleRouteChangeHandler } from "@app/utils/utils";
import { Dashboard } from "@app/Dashboard/Dashboard";
import { Ads } from "@app/Ads/Ads";
import { Campaigns } from "@app/Campaigns/Campaigns";
import { Organizations } from "@app/Organizations/Organizations";
import { NotFound } from "@app/NotFound/NotFound";
import { PlanComponent } from "@app/Subscription/PlanComponent";
import { Analytics } from "@app/Analytics/Analytics";
import { Settings } from "@app/Settings/Settings";
import { HelpCenter } from "@app/Help/HelpCenter";
import { AdDetailPage } from "@app/Ads/AdDetailPage";
import { CreateAdPage } from "@app/Ads/CreateAdPage";
import { CampaignDetailPage } from "@app/Campaigns/CampaignDetailPage";
import { useDocumentTitle } from "@app/utils/useDocumentTitle";
import { FaBuilding } from "react-icons/fa";
import { CubesIcon } from "@patternfly/react-icons";
import { useRef, useEffect } from "react";
import { useLocation } from "react-router-dom";

const LazySupport = React.lazy(() => import(/* webpackChunkName: 'support' */ "@app/Support/Support").then(m => ({ default: m.Support })));

let routeFocusTimer: number;

export interface IAppRoute {
  label?: string;
  component: React.ComponentType<any>;
  icon?: any;
  path: string;
  title: string;
  isAsync?: boolean;
}

const routes: IAppRoute[] = [
  {
    component: Dashboard,
    icon: null,
    label: "Dashboard",
    path: "/",
    title: "Main Dashboard"
  },
  {
    component: CreateAdPage,
    path: "/ads/new",
    title: "Create Ad"
  },
  {
    component: AdDetailPage,
    path: "/ads/:adId",
    title: "Ad Details"
  },
  {
    component: Ads,
    icon: CubesIcon,
    isAsync: true,
    label: "Ads",
    path: "/ads",
    title: "Ads Dashboard"
  },
  {
    component: CampaignDetailPage,
    path: "/campaigns/:campaignId",
    title: "Campaign Details"
  },
  {
    component: Campaigns,
    icon: null,
    label: "Campaigns",
    path: "/campaigns",
    title: "Campaigns Dashboard"
  },
  {
    component: Organizations,
    icon: FaBuilding,
    label: "Organizations",
    path: "/organizations",
    title: "Organizations"
  },
  {
    component: PlanComponent,
    icon: null,
    isAsync: true,
    label: "Plan",
    path: "/plan",
    title: "Plan"
  },
  {
    component: Analytics,
    icon: null,
    isAsync: true,
    label: "Analytics",
    path: "/analytics",
    title: "Analytics"
  },
  {
    component: HelpCenter,
    icon: null,
    label: "Help",
    path: "/help",
    title: "Help Center"
  },
  {
    component: Settings,
    icon: null,
    path: "/settings",
    title: "Settings"
  }
];

// a custom hook for sending focus to the primary content container
// after a view has loaded so that subsequent press of tab key
// sends focus directly to relevant content
const useA11yRouteChange = (isAsync: boolean) => {
  const location = useLocation();
  const prevLocationRef = useRef(location);

  useEffect(() => {
    if (prevLocationRef.current.pathname !== location.pathname) {
      if (!isAsync) {
        routeFocusTimer = accessibleRouteChangeHandler();
      }
      prevLocationRef.current = location;
    }
    return () => {
      clearTimeout(routeFocusTimer);
    };
  }, [isAsync, location]);
};

const RouteWithTitleUpdates = ({
  component: Component,
  isAsync = false,
  title,
}: IAppRoute) => {
  useA11yRouteChange(isAsync);
  useDocumentTitle(title);

  return <Component />;
};

const PageNotFound = ({ title }: { title: string }) => {
  useDocumentTitle(title);
  return <NotFound />;
};

const AppRoutes = () => (
  <Routes>
    {routes.map(({ path, component, title, isAsync, icon }, idx) => (
      <Route
        path={path}
        key={idx}
        element={
          <RouteWithTitleUpdates
            path={path}
            component={component}
            icon={icon}
            title={title}
            isAsync={isAsync}
          />
        }
      />
    ))}
    {/* Legacy wallet URL — keep working for any bookmarks/links */}
    <Route path="/wallet" element={<Navigate to="/plan" replace />} />
    <Route path="*" element={<PageNotFound title={"404 Page Not Found"} />} />
  </Routes>
);

export { AppRoutes, routes };
